import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatchFooter } from '../contexts/DispatchFooterContext';

interface SyncStatusFooterProps {
  sidebarCollapsed?: boolean;
  isTestEnvironment?: boolean;
}

const SyncStatusFooter: React.FC<SyncStatusFooterProps> = ({ sidebarCollapsed = false, isTestEnvironment = false }) => {
  const [isMobile, setIsMobile] = useState(false);
  const { dispatchButton, iconSizeSlider, paginationControls, globalFilters, refreshCountdown } = useDispatchFooter();
  const location = useLocation();
  const isAtRiskMonitor = location.pathname === '/dispatching/at-risk-monitor';
  const isSmartPreplanning = location.pathname === '/dispatching/smart-preplanning';

  // Check if we're on mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Use theme colors
  const bgClasses = 'bg-theme-bg-secondary';
  const borderClasses = 'border-theme-border-primary';
  const textClasses = 'text-body';

  return (
    <div
      className={`border-t px-2 sm:px-4 lg:px-8 pt-0 pb-[2px] shadow-lg transition-all flex items-center gap-2 sm:gap-4 ${bgClasses} ${borderClasses} ${textClasses}`}
      style={{
        height: '3rem'
      }}
    >
      {/* Refresh Countdown (left side) */}
      {refreshCountdown !== null && (
        <div className="text-xs text-muted">
          Refreshing in: {refreshCountdown}s
        </div>
      )}

      {/* Pagination Controls */}
      {paginationControls && (
        <>
          {paginationControls}
        </>
      )}

      {/* Icon Size Slider (left side) */}
      {iconSizeSlider && !paginationControls && (
        <div className="flex items-center gap-4">
          {iconSizeSlider}
        </div>
      )}

      {/* Dispatch Button (full width) - only show if no pagination */}
      {!paginationControls && dispatchButton && (
        <div className="flex-1">
          {dispatchButton}
        </div>
      )}

      {/* Spacer to push items to ends (only when no dispatchButton) */}
      {(paginationControls || !dispatchButton) && <div className="flex-1" />}

      {/* At Risk Monitor - Logic Breakdown link */}
      {isAtRiskMonitor && (
        <a
          href="/at-risk-monitor-rules.html"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-gray-400 dark:text-gray-500 hover:text-blue-500 hover:underline"
        >
          Logic Breakdown
        </a>
      )}

      {/* Smart Preplanning - Logic Breakdown link */}
      {isSmartPreplanning && (
        <a
          href="/smart-preplanning-rules.html"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-gray-400 dark:text-gray-500 hover:text-blue-500 hover:underline"
        >
          Logic Breakdown
        </a>
      )}

      {/* Global Filters (right side) */}
      {globalFilters && (
        <div className="flex items-center gap-2">
          {/* Portal target for dispatch planner filters */}
          {globalFilters === 'dispatch-planner-filters' ? (
            <div id="dispatch-planner-filters-portal" className="flex items-center gap-2" />
          ) : (
            globalFilters
          )}
        </div>
      )}
    </div>
  );
};

export default SyncStatusFooter;
